import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useActionChoices, useActionIdentifiers } from "stores";
import {
  actionInputName,
  actionFormValue,
} from "components/action-modal/utils/inputName";
import { TASK_INPUT_NAMES } from "constants/action-form";
import { OPERATIONS, TASKS } from "constants/actions";
import { FormSelectInput } from "form";
import { useFormikContext } from "formik";

// Only used to keep the existing, already-translated copy for Plowing/Mulch;
// any other operation with a populated typeVariants list falls back to the
// generic label below. This is a copy lookup only, not a rendering gate.
const OTHER_VARIANT_LABEL_KEYS = {
  [OPERATIONS.OTHER.PLOWING]: "Plowing",
  [OPERATIONS.OTHER.MULCH]: "Mulch",
};

const getIdentifierId = (identifier) => {
  if (typeof identifier === "string") return identifier;

  return (
    identifier?.actionIdentifier ??
    identifier?.actionIdentifierId ??
    identifier?.identifierId ??
    identifier?.id
  );
};

const getActionIdentifierIds = (source) => {
  const identifiers = [
    source?.actionIdentifierIds,
    source?.actionIdentifiers,
    source?.identifierIds,
    source?.actionIdentifier,
    source?.actionIdentifierId,
    source?.identifierId,
  ].find((value) => (Array.isArray(value) ? value.length : Boolean(value)));

  return [
    ...new Set(
      (Array.isArray(identifiers) ? identifiers : [identifiers])
        .map(getIdentifierId)
        .filter(Boolean),
    ),
  ];
};

const areMetaValuesEqual = (currentValue, nextValue) => {
  const normalize = (value) =>
    (Array.isArray(value) ? value : [value]).filter(Boolean).sort();
  const currentValues = normalize(currentValue);
  const nextValues = normalize(nextValue);

  return (
    currentValues.length === nextValues.length &&
    currentValues.every((value, index) => value === nextValues[index])
  );
};

const OtherOperationVariant = ({
  helperText,
  isProposed,
  isViewMode,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const { getActionById, getOperationById } = useActionChoices();
  const { identifiers: actionIdentifiersCatalog } = useActionIdentifiers();
  const catalogManagedFilterTask = useRef();

  const selectedAction = useMemo(
    () => getActionById(values?.taskId, true),
    [getActionById, values?.taskId],
  );
  const task = selectedAction?.task;
  const taskInputName = TASK_INPUT_NAMES[task];

  const selectedOperationTypeId = useMemo(
    () => actionFormValue(values, taskInputName, "operationTypeId", isProposed),
    [values, taskInputName, isProposed],
  );

  const selectedOperation = useMemo(
    () => getOperationById(selectedOperationTypeId, true),
    [getOperationById, selectedOperationTypeId],
  );

  const { typeVariants, typeVariantFieldName } = selectedOperation || {};
  const hasCatalogVariants =
    Boolean(typeVariantFieldName) && typeVariants?.length > 0;

  // Operations with no predefined options list in the catalog (e.g. Sowing)
  // can still carry a technique straight from the camvio-action-plan
  // suggestion. Fall back to a single, pre-selected option built from it so
  // it isn't silently dropped.
  const actionTechniqueId = actionFormValue(
    values,
    taskInputName,
    "actionTechniqueId",
    isProposed,
  );
  const actionTechniqueName = actionFormValue(
    values,
    taskInputName,
    "actionTechniqueName",
    isProposed,
  );
  const agroMetaValue = actionFormValue(
    values,
    taskInputName,
    "agroMetaValue",
    isProposed,
  );
  const isFilterableTask = task === TASKS.drug || task === TASKS.fertilize;

  // A stale technique from a previous operation shouldn't linger once the
  // operation changes underneath it (same idea as the pesticide/composition/
  // brandName reset chain in useDrugFormUtils).
  const previousOperationTypeId = useRef(selectedOperationTypeId);
  useEffect(() => {
    if (previousOperationTypeId.current === selectedOperationTypeId) return;
    previousOperationTypeId.current = selectedOperationTypeId;
    if (!taskInputName) return;
    if (actionTechniqueId) {
      setFieldValue(
        actionInputName(taskInputName, "actionTechniqueId", isProposed),
        undefined,
      );
    }
    if (isFilterableTask && agroMetaValue !== undefined) {
      setFieldValue(
        actionInputName(taskInputName, "agroMetaValue", isProposed),
        undefined,
      );
    }
  }, [
    selectedOperationTypeId,
    taskInputName,
    actionTechniqueId,
    agroMetaValue,
    isFilterableTask,
    isProposed,
    setFieldValue,
  ]);

  const isActionTechniqueField = typeVariantFieldName === "actionTechniqueId";
  const selectedTechniqueVariant = typeVariants?.find(
    (variant) => variant.value === actionTechniqueId,
  );
  const resolvedIdentifierIds = useMemo(() => {
    const identifierScopes = [
      actionTechniqueId && getActionIdentifierIds(selectedTechniqueVariant),
      getActionIdentifierIds(selectedOperation),
      getActionIdentifierIds(selectedAction),
    ];

    return identifierScopes.find((identifiers) => identifiers?.length) || [];
  }, [
    actionTechniqueId,
    selectedAction,
    selectedOperation,
    selectedTechniqueVariant,
  ]);
  const resolvedAgroMetaValue = useMemo(() => {
    if (!actionIdentifiersCatalog) return undefined;

    const metadataValues = [
      ...new Set(
        resolvedIdentifierIds
          .map(
            (identifierId) =>
              actionIdentifiersCatalog.find(
                (identifier) => identifier.id === identifierId,
              )?.value,
          )
          .filter(Boolean),
      ),
    ];

    if (metadataValues.length === 1) return metadataValues[0];
    return metadataValues.length ? metadataValues : undefined;
  }, [actionIdentifiersCatalog, resolvedIdentifierIds]);

  useEffect(() => {
    if (!taskInputName || !isFilterableTask || !actionIdentifiersCatalog) {
      return;
    }

    const wasManagedByCatalog =
      catalogManagedFilterTask.current === taskInputName;
    const hasSelectedCatalogTechnique = Boolean(
      isActionTechniqueField && actionTechniqueId && selectedTechniqueVariant,
    );
    const shouldManageFilter =
      wasManagedByCatalog ||
      agroMetaValue === undefined ||
      agroMetaValue === null ||
      hasSelectedCatalogTechnique;

    if (!shouldManageFilter) return;

    if (!areMetaValuesEqual(agroMetaValue, resolvedAgroMetaValue)) {
      setFieldValue(
        actionInputName(taskInputName, "agroMetaValue", isProposed),
        resolvedAgroMetaValue,
      );
    }

    catalogManagedFilterTask.current = taskInputName;
  }, [
    taskInputName,
    isFilterableTask,
    isActionTechniqueField,
    actionTechniqueId,
    selectedTechniqueVariant,
    actionIdentifiersCatalog,
    agroMetaValue,
    resolvedAgroMetaValue,
    isProposed,
    setFieldValue,
  ]);

  if (!hasCatalogVariants && !actionTechniqueId) {
    return null;
  }

  const operationTypeInputName = actionInputName(
    taskInputName,
    hasCatalogVariants ? typeVariantFieldName : "actionTechniqueId",
    isProposed,
  );
  const options = hasCatalogVariants
    ? typeVariants
    : [
        {
          value: actionTechniqueId,
          label: actionTechniqueName || actionTechniqueId,
        },
      ];

  const operationKey = getOperationById(selectedOperationTypeId);
  const labelSegment = OTHER_VARIANT_LABEL_KEYS[operationKey];
  const placeholder = labelSegment
    ? t(`OperationInput.OtherVariant.${labelSegment}.Placeholder`)
    : t("OperationInput.OtherVariant.Generic.Placeholder");

  return (
    <FormSelectInput
      name={operationTypeInputName}
      placeholder={placeholder}
      helperText={helperText}
      shouldHaveErrorBehaviorAfterSubmit
      isDisabled={isDisabled}
      isViewMode={isViewMode}
      options={options}
    />
  );
};

export default OtherOperationVariant;
